import { Conversation, Id } from '@nextforce/api'
import { useRoutingContext } from 'app/routing_context'
import { useConversations } from 'chat/conversations/queries/list'
import { isRepository } from 'chat/conversations/utils'
import { getDaysSinceOldestMessage } from 'chat/shared/util'
import { SearchMessagesScreenProps } from 'home/stack/types'
import React, { useCallback, useState } from 'react'
import { StatusBoundary } from 'shared/components/status_boundary'
import { useLoadEarlierMessages, useNonDeletedMessages } from '../queries/list'
import { SearchView } from './view'

export function SearchMessagesScreen({
  navigation,
}: SearchMessagesScreenProps) {
  const { handleNavigation } = useRoutingContext()
  const {
    data: messagesData,
    isLoading: isMessagesLoading,
    error: messagesError,
  } = useNonDeletedMessages(data => data)
  const {
    data: conversationsData,
    isLoading: isConversationsLoading,
    error: conversationsError,
  } = useConversations()
  const { loadEarlier } = useLoadEarlierMessages()
  const [isLoadingOlder, setIsLoadingOlder] = useState(false)

  const conversations: Conversation[] | undefined = conversationsData?.map(
    conversation => conversation
  )

  const messages = messagesData?.messages
  const oldestEventId = messagesData?.oldestEventId

  const cachedDays = getDaysSinceOldestMessage(oldestEventId)

  const handleLoadOlderMessages = useCallback(async () => {
    setIsLoadingOlder(true)
    try {
      await loadEarlier()
    } catch (error) {
      console.error('[SearchMessages] Failed to load older messages:', error)
    } finally {
      setIsLoadingOlder(false)
    }
  }, [loadEarlier])

  const onPressConversation = useCallback(
    (conversation: Conversation, messageId?: Id) => {
      if (isRepository(conversation)) {
        handleNavigation({
          routeName: 'file_folders_bottom_tab',
          routeParams: {
            screen: 'files',
            params: { conversationId: conversation.id },
            conversationId: conversation.id,
          },
        })
      } else {
        handleNavigation({
          routeName: 'messages',
          routeParams: {
            id: conversation.id,
            messageId,
          },
        })
      }
    },
    [handleNavigation]
  )

  return (
    <StatusBoundary
      isLoading={isConversationsLoading || isMessagesLoading}
      error={conversationsError || messagesError}
    >
      <SearchView
        conversations={conversations}
        messages={messages}
        onPressConversation={onPressConversation}
        cachedDays={cachedDays}
        onLoadOlderMessages={handleLoadOlderMessages}
        isLoadingOlder={isLoadingOlder}
      />
    </StatusBoundary>
  )
}
